import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DisponibilidadConfig, Agendamiento, Feriado } from '@/lib/types'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

const ACCENT = '#342CDE'
const DAY_HEADERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
const MONTH_NAMES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

function toDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatDateEs(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('es-PE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function buildCalendarUrl(fecha: string, hora: string, duracion: number, nombre: string): string {
  const [y, m, d] = fecha.split('-').map(Number)
  const [hh, mm] = hora.split(':').map(Number)
  const start = new Date(y, m - 1, d, hh, mm)
  const end = new Date(start.getTime() + duracion * 60_000)
  const fmt = (dt: Date) =>
    dt.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(nombre)}&dates=${fmt(start)}/${fmt(end)}`
}

export default function AgendarPublico() {
  const [step, setStep] = useState(1)
  const [config, setConfig] = useState<DisponibilidadConfig | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [slots, setSlots] = useState<string[]>([])
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    notas: '',
  })
  const [confirmed, setConfirmed] = useState<Agendamiento | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date()
    return { year: d.getFullYear(), month: d.getMonth() }
  })
  const [searchParams] = useSearchParams()
  const ref = searchParams.get('ref')
  const [feriadosList, setFeriadosList] = useState<Feriado[]>([])

  /* ── Load config ── */
  useEffect(() => {
    setLoading(true)
    fetch(`${API_URL}/api/booking/config`)
      .then((r) => {
        if (!r.ok) throw new Error('No se pudo cargar la configuración')
        return r.json()
      })
      .then((data) => setConfig(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))

    // Load public holidays
    fetch(`${API_URL}/api/booking/feriados/public`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setFeriadosList(Array.isArray(data) ? data : []))
      .catch(() => { /* ignore */ })
  }, [])

  /* ── Calendar helpers ── */
  const todayStr = useMemo(() => toDateStr(new Date()), [])

  const maxDateStr = useMemo(() => {
    if (!config) return ''
    const d = new Date()
    d.setDate(d.getDate() + config.max_dias_adelanto)
    return toDateStr(d)
  }, [config])

  const calendarDays = useMemo(() => {
    const { year, month } = viewMonth
    const firstDay = new Date(year, month, 1).getDay()
    // Adjust so Monday = 0: Sun(0)->6, Mon(1)->0, Tue(2)->1 …
    const startOffset = (firstDay + 6) % 7
    const daysInMonth = new Date(year, month + 1, 0).getDate()

    const cells: Array<{ day: number; dateStr: string } | null> = []
    for (let i = 0; i < startOffset; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      cells.push({ day: d, dateStr })
    }
    return cells
  }, [viewMonth])

  function isDayAvailable(dateStr: string): boolean {
    if (!config) return false
    if (dateStr < todayStr || dateStr > maxDateStr) return false
    const [y, m, d] = dateStr.split('-').map(Number)
    const dow = new Date(y, m - 1, d).getDay() // 0=Sun
    if (!config.dias_disponibles.includes(dow)) return false
    // Check if it's a holiday
    if (isFeriado(dateStr)) return false
    return true
  }

  function isFeriado(dateStr: string): boolean {
    const [, m, d] = dateStr.split('-').map(Number)
    return feriadosList.some((f) => {
      if (f.fecha === dateStr) return true
      if (f.recurrente) {
        const [, fm, fd] = f.fecha.split('-').map(Number)
        return fm === m && fd === d
      }
      return false
    })
  }

  function getFeriadoName(dateStr: string): string | null {
    const [, m, d] = dateStr.split('-').map(Number)
    const match = feriadosList.find((f) => {
      if (f.fecha === dateStr) return true
      if (f.recurrente) {
        const [, fm, fd] = f.fecha.split('-').map(Number)
        return fm === m && fd === d
      }
      return false
    })
    return match ? match.nombre : null
  }

  async function handleDateClick(dateStr: string) {
    if (!isDayAvailable(dateStr)) return
    setSelectedDate(dateStr)
    setSelectedSlot(null)
    setError(null)
    try {
      const res = await fetch(`${API_URL}/api/booking/slots/${dateStr}`)
      if (!res.ok) throw new Error('Error al cargar horarios')
      const data = await res.json()
      setSlots(data)
      setStep(2)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    }
  }

  /* ── Submit ── */
  async function handleSubmit() {
    if (!selectedDate || !selectedSlot) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch(`${API_URL}/api/booking/agendar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fecha: selectedDate,
          hora_inicio: selectedSlot,
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono || null,
          empresa: formData.empresa || null,
          notas: formData.notas || null,
          ref: ref || null,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.error || 'Error al agendar la reunión')
      }
      const data: Agendamiento = await res.json()
      setConfirmed(data)
      setStep(4)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setSubmitting(false)
    }
  }

  function resetFlow() {
    setStep(1)
    setSelectedDate(null)
    setSelectedSlot(null)
    setSlots([])
    setFormData({ nombre: '', email: '', telefono: '', empresa: '', notas: '' })
    setConfirmed(null)
    setError(null)
  }

  /* ── Shared styles ── */
  const inputStyle: React.CSSProperties = {
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: 10,
    fontSize: 15,
    color: '#1a1a2e',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
  }

  function inputFocusHandlers() {
    return {
      onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.style.borderColor = ACCENT
        e.target.style.boxShadow = '0 0 0 3px rgba(52,44,222,0.1)'
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.style.borderColor = '#ddd'
        e.target.style.boxShadow = 'none'
      },
    }
  }

  /* ── Loading / error states ── */
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 15 }}>Cargando…</div>
      </div>
    )
  }

  if (!config && error) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#e74c3c', fontSize: 15 }}>{error}</div>
      </div>
    )
  }

  /* ── Render ── */
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px',
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: '100%',
          background: '#ffffff',
          borderRadius: 20,
          padding: 40,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        {/* Logo */}
        <img
          src="/assets/brand/logo-full-color.svg"
          alt="1conecta"
          style={{ height: 28, marginBottom: 24 }}
        />

        {/* Error banner */}
        {error && (
          <div
            style={{
              padding: '10px 14px',
              background: '#fff0f0',
              color: '#e74c3c',
              borderRadius: 10,
              fontSize: 14,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* ═══════ STEP 1: Calendar ═══════ */}
        {step === 1 && config && (
          <div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: '#1a1a2e',
                margin: '0 0 4px',
              }}
            >
              {config.nombre}
            </h1>
            <p
              style={{
                fontSize: 14,
                color: '#888',
                margin: '0 0 24px',
              }}
            >
              {config.duracion_minutos} minutos
            </p>

            {/* Month nav */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}
            >
              <button
                onClick={() =>
                  setViewMonth((p) => {
                    const m = p.month - 1
                    return m < 0
                      ? { year: p.year - 1, month: 11 }
                      : { year: p.year, month: m }
                  })
                }
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                  color: '#555',
                  padding: '4px 8px',
                }}
                aria-label="Mes anterior"
              >
                ←
              </button>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#1a1a2e',
                  textTransform: 'capitalize',
                }}
              >
                {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
              </span>
              <button
                onClick={() =>
                  setViewMonth((p) => {
                    const m = p.month + 1
                    return m > 11
                      ? { year: p.year + 1, month: 0 }
                      : { year: p.year, month: m }
                  })
                }
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                  color: '#555',
                  padding: '4px 8px',
                }}
                aria-label="Mes siguiente"
              >
                →
              </button>
            </div>

            {/* Day headers */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                textAlign: 'center',
                marginBottom: 4,
              }}
            >
              {DAY_HEADERS.map((h, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#aaa',
                    padding: '6px 0',
                  }}
                >
                  {h}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 4,
                justifyItems: 'center',
              }}
            >
              {calendarDays.map((cell, i) => {
                if (!cell) return <div key={`e-${i}`} />
                const available = isDayAvailable(cell.dateStr)
                const isToday = cell.dateStr === todayStr
                const isSelected = cell.dateStr === selectedDate

                return (
                  <button
                    key={cell.dateStr}
                    onClick={() => handleDateClick(cell.dateStr)}
                    disabled={!available}
                    title={getFeriadoName(cell.dateStr) || undefined}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: isToday ? 700 : 400,
                      border: isToday && !isSelected ? `2px solid ${ACCENT}` : '2px solid transparent',
                      background: isSelected ? ACCENT : 'transparent',
                      color: isSelected ? '#fff' : available ? '#1a1a2e' : '#ccc',
                      cursor: available ? 'pointer' : 'not-allowed',
                      transition: 'all 0.15s',
                      fontFamily: 'inherit',
                      position: 'relative',
                    }}
                  >
                    {cell.day}
                    {isFeriado(cell.dateStr) && (
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 2,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: '#e74c3c',
                        }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* ═══════ STEP 2: Time Slots ═══════ */}
        {step === 2 && selectedDate && (
          <div>
            <p
              style={{
                fontSize: 15,
                color: '#555',
                margin: '0 0 20px',
                textTransform: 'capitalize',
              }}
            >
              {formatDateEs(selectedDate)}
            </p>

            {slots.length === 0 ? (
              <p style={{ color: '#888', fontSize: 14 }}>
                No hay horarios disponibles para este día.
              </p>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 10,
                }}
              >
                {slots.map((slot) => {
                  const active = selectedSlot === slot
                  return (
                    <button
                      key={slot}
                      onClick={() => {
                        setSelectedSlot(slot)
                        setStep(3)
                      }}
                      style={{
                        padding: '12px 16px',
                        borderRadius: 10,
                        border: `2px solid ${active ? ACCENT : 'rgba(52,44,222,0.2)'}`,
                        background: active ? ACCENT : '#fff',
                        color: active ? '#fff' : ACCENT,
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        fontFamily: 'inherit',
                      }}
                    >
                      {slot}
                    </button>
                  )
                })}
              </div>
            )}

            <button
              onClick={() => {
                setStep(1)
                setError(null)
              }}
              style={{
                marginTop: 24,
                background: 'none',
                border: 'none',
                color: ACCENT,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              ← Atrás
            </button>
          </div>
        )}

        {/* ═══════ STEP 3: Form ═══════ */}
        {step === 3 && selectedDate && selectedSlot && (
          <div>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#1a1a2e',
                margin: '0 0 8px',
              }}
            >
              Tus datos
            </h2>
            <p
              style={{
                fontSize: 14,
                color: '#888',
                margin: '0 0 24px',
                textTransform: 'capitalize',
              }}
            >
              {formatDateEs(selectedDate)} · {selectedSlot}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>
                  Nombre <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData((p) => ({ ...p, nombre: e.target.value }))}
                  style={inputStyle}
                  placeholder="Tu nombre completo"
                  required
                  {...inputFocusHandlers()}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>
                  Email <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                  style={inputStyle}
                  placeholder="correo@empresa.com"
                  required
                  {...inputFocusHandlers()}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData((p) => ({ ...p, telefono: e.target.value }))}
                  style={inputStyle}
                  placeholder="+51 999 999 999"
                  {...inputFocusHandlers()}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>
                  Empresa
                </label>
                <input
                  type="text"
                  value={formData.empresa}
                  onChange={(e) => setFormData((p) => ({ ...p, empresa: e.target.value }))}
                  style={inputStyle}
                  placeholder="Nombre de tu empresa"
                  {...inputFocusHandlers()}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>
                  Notas
                </label>
                <textarea
                  value={formData.notas}
                  onChange={(e) => setFormData((p) => ({ ...p, notas: e.target.value }))}
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  placeholder="¿Algo que debamos saber?"
                  {...inputFocusHandlers()}
                />
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !formData.nombre.trim() || !formData.email.trim()}
              style={{
                marginTop: 24,
                width: '100%',
                padding: 14,
                background: submitting ? '#8884d8' : ACCENT,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                fontFamily: 'inherit',
              }}
            >
              {submitting ? 'Agendando…' : 'Confirmar Reunión'}
            </button>

            <button
              onClick={() => {
                setStep(2)
                setSelectedSlot(null)
                setError(null)
              }}
              style={{
                marginTop: 12,
                background: 'none',
                border: 'none',
                color: ACCENT,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'block',
                fontFamily: 'inherit',
              }}
            >
              ← Atrás
            </button>
          </div>
        )}

        {/* ═══════ STEP 4: Confirmation ═══════ */}
        {step === 4 && confirmed && (
          <div style={{ textAlign: 'center' }}>
            {/* Green checkmark */}
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#00b894',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h2
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: '#1a1a2e',
                margin: '0 0 16px',
              }}
            >
              ¡Reunión agendada!
            </h2>

            <p
              style={{
                fontSize: 15,
                color: '#555',
                margin: '0 0 6px',
                textTransform: 'capitalize',
              }}
            >
              {formatDateEs(confirmed.fecha)}
            </p>
            <p style={{ fontSize: 15, color: '#555', margin: '0 0 16px' }}>
              {confirmed.hora_inicio} – {confirmed.hora_fin}
            </p>

            {confirmed.google_meet_url && (
              <a
                href={confirmed.google_meet_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  background: '#f0f0ff',
                  color: ACCENT,
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  marginBottom: 12,
                }}
              >
                Unirse a Google Meet →
              </a>
            )}

            {config && (
              <div style={{ marginBottom: 20 }}>
                <a
                  href={buildCalendarUrl(
                    confirmed.fecha,
                    confirmed.hora_inicio,
                    config.duracion_minutos,
                    config.nombre,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 14,
                    color: ACCENT,
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  📅 Agregar a Google Calendar
                </a>
              </div>
            )}

            <button
              onClick={resetFlow}
              style={{
                padding: '12px 28px',
                background: '#f8f9fa',
                color: '#555',
                border: '1px solid #ddd',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Agendar otra reunión
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
